import { FormErrorData } from "../../../../../../api-client/APIClient";
import {
  BulkScopeEdit,
  bulkEditCommonModels,
} from "../../../../../../api-client/categories/CommonModelTogglesAPIClient";
import { APICategory } from "../../../../../../models/Entities";
import { EnabledAction } from "../../../../../../models/CommonModel";
import {
  createSortedModelsMap,
  ModelWithSortedFields,
  SortedModelsMap,
} from "../../CommonModelUtils";
import { ScopeEdit } from "../types";
import { buildFieldUpdate, buildModelCapabilityUpdate, ScopeUpdateArgs } from "./scopeMapping";

const GENERIC_SCOPE_SAVE_ERROR =
  "Something went wrong, please check your connection and try again.";

/**
 * Resolves a failed scope-save Response into a user-facing message. Returns the message rather than
 * toasting it, so the batched save can gather messages across every failed edit and surface them
 * together:
 *   - 403: a permission message, or the permanently-disabled message when the model can't be
 *     re-enabled.
 *   - 400 carrying non_field_errors (the single-edit shape, and the bulk view's own structural
 *     errors): the backend's message (e.g. "the X model must be enabled before the Y field can be
 *     enabled"), surfaced verbatim.
 *   - 400 carrying a per-item error list (DRF many=True validation): the first field error in the
 *     batch, surfaced verbatim.
 *   - anything else / unparseable body: the generic connection message.
 */
const resolveScopeSaveErrorMessage = async (
  error: Response | undefined,
  model: ModelWithSortedFields | undefined,
): Promise<string> => {
  if (!error) return GENERIC_SCOPE_SAVE_ERROR;
  // On 403 show the permanently-disabled reason (when the batch touched such a model, resolved by
  // the caller) or a generic permission message; the 403 body is not read.
  if (error.status === 403) {
    return model?.is_permanently_disabled
      ? "Common model is permanently disabled and cannot be re-enabled"
      : "You do not have permission to perform this action";
  }
  try {
    const data = await error.json();
    // The bulk endpoint validates many=True, so field validation errors come back as a per-item list
    // (e.g. [{}, { enabled_actions: ["..."] }]) rather than the single endpoint's
    // { non_field_errors: [...] }. Handle both.
    if (Array.isArray(data)) {
      for (const itemErrors of data) {
        const firstFieldErrors = Object.values(itemErrors ?? {})[0];
        if (Array.isArray(firstFieldErrors) && firstFieldErrors.length) {
          return String(firstFieldErrors[0]);
        }
      }
    } else {
      const formError = data as FormErrorData;
      if (formError?.non_field_errors?.length) return formError.non_field_errors[0];
    }
  } catch {
    // Unparseable body — fall through to the generic message.
  }
  return GENERIC_SCOPE_SAVE_ERROR;
};

export interface SaveScopeEditsResult {
  models: SortedModelsMap;
  /** De-duplicated, user-facing messages for the edits that failed; empty when every edit saved. */
  errors: string[];
}

/**
 * Flushes all pending scope edits in a single atomic bulk PATCH. Each element is built from the
 * already-edited (final) model state, so the untouched capability on a model is preserved and edits
 * on the same model converge regardless of order. The backend applies the batch all-or-nothing: on
 * success it returns the full category list; on failure nothing is saved and the base models are
 * returned unchanged alongside one error message.
 *
 * The batch sends object edits before field edits: the endpoint walks the batch one edit at a time
 * and validates each against the state earlier edits wrote, so a field edit that references another
 * object is rejected ("The X model must be enabled before the Y field on Z can be enabled") unless
 * the edit enabling X lands first.
 */
export const saveScopeEdits = async (
  category: APICategory,
  baseModels: SortedModelsMap,
  editedModels: SortedModelsMap,
  edits: ScopeEdit[],
): Promise<SaveScopeEditsResult> => {
  const scopes: BulkScopeEdit[] = [];

  const orderedEdits = [
    ...edits.filter((edit) => edit.kind === "model"),
    ...edits.filter((edit) => edit.kind === "field"),
  ];

  for (const edit of orderedEdits) {
    // Build from the final (edited) state so buildModelCapabilityUpdate preserves the capability
    // that this edit didn't touch.
    const model = editedModels[edit.modelName];
    if (!model) continue;

    const args: ScopeUpdateArgs =
      edit.kind === "model"
        ? buildModelCapabilityUpdate(model, edit.capability, edit.scope)
        : buildFieldUpdate(edit.modelName, edit.fieldName, edit.scope);

    let { modelName, fieldName } = args;

    // Mirror ConfigurationCommonModelsPage.updateEnabledStatus: a field related to a
    // subobject (related model without endpoints) is toggled on the related model itself.
    if (edit.kind === "field") {
      const relatedModelName = model.fields[edit.fieldName]?.related_to;
      const relatedModel = relatedModelName ? editedModels[relatedModelName] : undefined;
      const isRelatedToSubobject = relatedModel && !relatedModel.has_endpoints;
      if (relatedModelName && isRelatedToSubobject && relatedModel?.name !== edit.modelName) {
        modelName = relatedModelName;
        fieldName = undefined;
      }
    }

    scopes.push({
      name: modelName,
      field: fieldName,
      enabled_actions: args.enabledActions,
      optional_actions: args.optionalActions,
      non_optional_actions: args.nonOptionalActions,
      is_field_optional: args.isFieldConfigurable,
    });
  }

  // Collapse the built payloads to one entry per model (or per model+field). Toggling both READ and
  // WRITE on the same model produces two entries with the same `name` but different optional /
  // non_optional lists (each capability edit only names its own action), so an exact-match dedupe would
  // keep both and send the model twice. Merge them into a single payload by unioning the action lists:
  // one entry per model, so the CHANGED_SCOPES audit log lists the model once. Field edits are keyed by
  // name+field so distinct fields stay separate.
  const union = (a: EnabledAction[], b: EnabledAction[]): EnabledAction[] => [
    ...new Set([...a, ...b]),
  ];
  const mergedByKey = new Map<string, BulkScopeEdit>();
  for (const scope of scopes) {
    const key = scope.field ? `${scope.name}::${scope.field}` : scope.name;
    const existing = mergedByKey.get(key);
    if (!existing) {
      mergedByKey.set(key, scope);
    } else {
      existing.enabled_actions = union(existing.enabled_actions, scope.enabled_actions);
      existing.optional_actions = union(existing.optional_actions, scope.optional_actions);
      existing.non_optional_actions = union(
        existing.non_optional_actions,
        scope.non_optional_actions,
      );
    }
  }
  const dedupedScopes = [...mergedByKey.values()];

  if (dedupedScopes.length === 0) return { models: baseModels, errors: [] };

  const result = await bulkEditCommonModels({ category, scopes: dedupedScopes });
  if (result.status === "success") {
    return { models: createSortedModelsMap(result.data), errors: [] };
  }

  // Atomic failure: nothing was saved, so keep the base models and surface a single message. The
  // single-edit flow passed the model so a 403 could show the "permanently disabled" reason; the batch
  // has no single model, so surface that model if the batch touched a permanently-disabled one (which
  // can't be re-enabled and is the usual cause of a 403 here).
  const permanentlyDisabledModel = edits
    .map((edit) => baseModels[edit.modelName])
    .find((model) => model?.is_permanently_disabled);
  const message = await resolveScopeSaveErrorMessage(result.error, permanentlyDisabledModel);
  return { models: baseModels, errors: [message] };
};
